import { useSelector } from "react-redux"
import { Button, Card, Row, Col, Image } from 'react-bootstrap'
import { useState, useRef, useEffect } from "react"
import { useSwipeable } from 'react-swipeable';
import AgendaItemEditModal from './crudModals/AgendaItemEditModal';
import AgendaItemDeleteModal from './crudModals/AgendaItemDeleteModal';
import AgendaItemModal from './modals/AgendaItemModal';
import useUserAvatar from './useUserAvatar';
import AgendaItemTimerControls from './AgendaItemTimerControls';
import ActivityParticipant from './ActivityParticipant';
import DebateAgendaTimers from './DebateAgendaTimers';
import DebateCardDetails from './DebateCardDetails';
import useStoredToggle from './useStoredToggle';
import Limits from './modals/eventTypes/SharedComponents/Limits';
import { getActivityTimingLimits } from 'utils/activityTiming';

const getAgendaUserInitials = (user) => {
  const firstName = String(user?.firstName || '').trim();
  const lastName = String(user?.lastName || '').trim();

  if (!firstName && !lastName) {
    return '';
  }

  return `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();
};

export default function AgendaItemCard({
  agenda,
  getMeeting,
  meeting,
  agendaItem,
  dragHandleProps,
  editMode,
  showClockText = true,
  tableTopics = null,
  onOpenTableTopicsResults = null,
}) {
  const item = agendaItem
  const { avatarSrc: assignedAvatarSrc, onAvatarError: onAssignedAvatarError } = useUserAvatar(item?.assignedUser, meeting);
  const assignedUserInitials = getAgendaUserInitials(item?.assignedUser);

  // console.log('Rendering AgendaItemCard for item:', item);
  const { user: authUser } = useSelector(x => x.auth)
  const timerManagerId = String(meeting?.meetingDetails?.specialAssignments?.timerManager || meeting?.specialAssignments?.timerManager || '').trim();
  const isTimerManager = Boolean(
    authUser?.user && timerManagerId && [
      authUser.user.localUserId,
      authUser.user._id,
      authUser.user.id,
      authUser.user.tmAuthUserId,
    ].map((value) => String(value || '').trim()).includes(timerManagerId)
  );
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked);
  const agendaSnapshot = [...agenda];
  const isOwnTableTopicsSpeakerPage = tableTopics?.pages?.some(page => (
    String(page.agendaItemId) === String(item?.agendaItemId)
    && page.kind === 'speaker'
    && tableTopics.ownPageIds?.some(id => String(id) === String(page.agendaItemId))
  ));
  const hasPublishedTableTopicsEvaluation = isOwnTableTopicsSpeakerPage
    && tableTopics.reviews?.some(review => review.entries?.some(entry => (
      String(entry.agendaItemId) === String(item?.agendaItemId) && entry.publishedAt
    )));

  const [showModal, setShowModal] = useState(false);
  const [showCurrentSpeaker, toggleCurrentSpeaker] = useStoredToggle(item?.agendaItemId, 'current-speaker', false);
  const debateToggle = (label, shown, toggle, icon, shortLabel = label) => <Button variant="outline-secondary" size="sm"
    className="agenda-debate-toggle d-inline-flex align-items-center gap-2 px-3 text-nowrap"
    aria-pressed={shown} aria-label={`${shown ? 'Hide' : 'Show'} ${label}`} title={`${shown ? 'Hide' : 'Show'} ${label}`}
    onClick={(event) => {
      event.stopPropagation();
      toggle();
    }}>
    <i className={`bi ${icon}`} aria-hidden="true" />
    {`${shown ? 'Hide' : 'Show'} ${shortLabel}`}
  </Button>;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isVisibilitySaving, setIsVisibilitySaving] = useState(false);
  const swipeRef = useRef(false);
  const swipeTimeout = useRef(null);

  const [swipeDelta, setSwipeDelta] = useState(0);
  const [swiping, setSwiping] = useState(false);

  useEffect(() => {
    const handleTargetedDisplayCommand = () => setShowModal(false);
    window.addEventListener('meeting:targeted-display-command', handleTargetedDisplayCommand);
    return () => window.removeEventListener('meeting:targeted-display-command', handleTargetedDisplayCommand);
  }, []);

  const itemTitleLines = String(item?.item_title || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const speechTitleLine = itemTitleLines[0] || '';
  const pathwayProjectLine = itemTitleLines.slice(1).join(' ');
  const pathwayProjectMatch = pathwayProjectLine.match(/^(.*?)\s*(?:-\s*)?#(\d+)\s*(?:-\s*)?(.*)$/);
  const pathwayNameLine = pathwayProjectMatch ? (pathwayProjectMatch[1] || '').trim() : pathwayProjectLine;
  const projectDetailsLine = pathwayProjectMatch
    ? `#${pathwayProjectMatch[2]}${(pathwayProjectMatch[3] || '').trim() ? ` - ${(pathwayProjectMatch[3] || '').trim()}` : ''}`
    : '';

  const SWIPE_THRESHOLD = 120; // px

  const handleShowModal = () => {
    if (swipeRef.current) {
      clearTimeout(swipeTimeout.current);
      swipeTimeout.current = setTimeout(() => {
        swipeRef.current = false;
      }, 300);
      return;
    }
    setShowModal(true);
  }
  const handleCloseModal = () => setShowModal(false);

  const toggleTitleVisibility = async (event) => {
    event.stopPropagation();
    if (!isTimerManager || isVisibilitySaving || isMeetingLocked) return;
    setIsVisibilitySaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/agendaItem/${item.agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify({ hideTitleAndSubtitle: !item.hideTitleAndSubtitle }),
      });
      if (!response.ok) throw new Error('Unable to update agenda title visibility.');
      await Promise.resolve(getMeeting?.());
    } catch (error) {
      window.alert(error.message || 'Unable to update agenda title visibility.');
    } finally {
      setIsVisibilitySaving(false);
    }
  };

  const handleShowEditModal = () => setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);

  const handleShowDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleSwiping = (eventData) => {
    setSwiping(true);
    setSwipeDelta(eventData.deltaX);
  };

  const handleSwiped = (eventData) => {
    setSwiping(false);

    if (eventData.absX > SWIPE_THRESHOLD) {
      swipeRef.current = true; // Set the flag to block click
      if (eventData.deltaX > 0) {
        setSwipeDelta(0);
        handleShowEditModal();
      } else {
        setSwipeDelta(0);
        handleShowDeleteModal();
      }
    } else {
      setSwipeDelta(0);
      swipeRef.current = false;
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiping: handleSwiping,
    onSwiped: handleSwiped,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const visibilityToggle = isTimerManager ? <Button
    variant='link'
    size='sm'
    className='p-0 agenda-item-toggle-button'
    onClick={toggleTitleVisibility}
    disabled={isVisibilitySaving || isMeetingLocked}
    title={isMeetingLocked ? 'This meeting is locked' : item?.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
    aria-label={item?.hideTitleAndSubtitle ? 'Reveal title and subtitle' : 'Hide title and subtitle'}
  >
    <i className={`bi ${item?.hideTitleAndSubtitle ? 'bi-eye' : 'bi-eye-slash'} agenda-item-toggle-icon`} aria-hidden='true'></i>
  </Button> : null;

  return (
    <section className='agendaItemCard'>
      <div className="timeline-container">
        <div className="timeline-content" >
          <Row>
            <Col>
              <div
                style={{ position: 'relative' }}
                {...(authUser?.user.acl === "Officer" && editMode ? swipeHandlers : {})}
              >
                {/* Left action (swipe right) */}
                {editMode && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '100%',
                      background: swipeDelta > 0 ? '#198754' : 'transparent',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingLeft: 24,
                      zIndex: 0,
                      transition: 'background 0.2s'
                    }}
                  >
                    {swipeDelta > 30 && (
                      <span>
                        <i className="bi bi-pencil-square" style={{ fontSize: '1.5rem', marginRight: 8 }}></i>
                        Edit
                      </span>
                    )}
                  </div>
                )}
                {/* Right action (swipe left) */}
                {editMode && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '100%',
                      background: swipeDelta < 0 ? '#dc3545' : 'transparent',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: 24,
                      transition: 'background 0.2s'
                    }}
                  >
                    {swipeDelta < -30 && (
                      <span>
                        <i className="bi bi-trash" style={{ fontSize: '1.5rem', marginLeft: 8 }}></i>
                        Delete
                      </span>
                    )}
                  </div>
                )}
                {/* The card itself */}
                <div
                  style={{
                    position: 'relative',
                    transform: `translateX(${swipeDelta}px)`,
                    transition: swiping ? 'none' : 'transform 0.2s'
                  }}
                >
                  <Card
                    className='text-center next-meeting next-meeting-hints-style meeting-card-static mt-2 mb-2 agenda-item-card'
                    onClick={handleShowModal}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body>
                      <div className="next-meeting-content">
                        <div className='meetings-mock-avatar meetings-mock-avatar--agenda'>
                          {assignedAvatarSrc
                            ? <Image src={assignedAvatarSrc} alt='Assigned presenter avatar' onError={onAssignedAvatarError} />
                            : assignedUserInitials
                              ? (
                                <span
                                  className='meetings-mock-avatar-fallback'
                                  style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1 }}
                                >
                                  {assignedUserInitials}
                                </span>
                              )
                              : <i className='bi bi-person-fill meetings-mock-avatar-fallback'></i>}
                        </div>
                        <Row>
                          <Col>
                            <Row className='justify-content-center'>
                              <Col xs={12} md={12} lg={12} className='align-self-center agenda-item-text-col'>
                                <h6 className='mb-2' style={{ fontSize: '1.5vh' }} {...dragHandleProps}>
                                  <span className='small-message-subtext' style={{ fontSize: '1.5vh' }}>{item?.startTime}: </span>
                                  {item.meetingRole?.name ? `${item.meetingRole?.name}` : ''}
                                </h6>
                                <h5 className='not-found-title agenda-item-event-name mb-0' style={{ fontSize: '1.8vh' }}>
                                  {item?.hideTitleAndSubtitle ? item.agendaEvent?.name : (speechTitleLine || item.agendaEvent?.name)}
                                </h5>
                                {!item?.hideTitleAndSubtitle && pathwayNameLine && (
                                  <h6 className='mb-1'>{pathwayNameLine}</h6>
                                )}
                                {!item?.hideTitleAndSubtitle && projectDetailsLine && (
                                  <h6 className='mb-1'>{projectDetailsLine}</h6>
                                )}
                                <h6 className='mb-0' style={{ fontSize: '1.5vh' }}>
                                  {!item.assignedUser?.firstName
                                    ? 'TBA'
                                    : `${item.assignedUser?.firstName} ${item.assignedUser?.lastName}`}

                                </h6>
                                {item.activity?.type === 'panelDiscussion' && (
                                  <div className="mt-2" aria-label="Panelists">
                                    <small className="d-block">Panelists</small>
                                    <ul className="activity-participant-list small">
                                      {(item.activity.panelists || []).map((panelist) => (
                                        <li key={panelist.userId}><ActivityParticipant user={panelist} meeting={meeting} /></li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {item.activity?.type === 'debate' && <DebateCardDetails item={item} meeting={meeting} getMeeting={getMeeting}
                                  isTimerManager={isTimerManager} isLocked={isMeetingLocked} />}
                              </Col>
                            </Row>
                            {(item.activity?.type === 'debate' || item?.agendaEvent?.green || item?.agendaEvent?.amber || item?.agendaEvent?.red || isTimerManager) && (
                              <Row className='mt-3 align-items-center agenda-item-timer-row'>
                                {item.activity?.type !== 'debate' && <Col className='agenda-item-timer-limits-col' style={{ fontSize: '1.3vh' }}>
                                  <Row className='agenda-item-timer-limits-row'>
                                    {item?.agendaEvent?.green && <Col style={{ backgroundColor: '#146c43', color: '#ffffff', textAlign: 'center' }}>{item?.agendaEvent?.green}</Col>}
                                    {item?.agendaEvent?.amber && <Col style={{ backgroundColor: '#ffc107', color: '#000000', textAlign: 'center' }}>{item?.agendaEvent?.amber}</Col>}
                                    {item?.agendaEvent?.red && <Col style={{ backgroundColor: '#dc3545', color: '#ffffff', textAlign: 'center' }}>{item?.agendaEvent?.red}</Col>}
                                  </Row>
                                </Col>}
                                {isTimerManager && item.activity?.type !== 'debate' && (
                                  <Col xs='auto' className='agenda-item-toggle-col'>
                                    {visibilityToggle}
                                  </Col>
                                )}
                                {item.activity?.type === 'debate' && !isTimerManager ? (
                                  <Col className='agenda-item-timer-limits-col' role="group" aria-label="Entire debate timing limits">
                                    <Limits item={{ agendaEvent: getActivityTimingLimits(item.activity) || item.agendaEvent }} readOnly compact />
                                  </Col>
                                ) : <Col xs={item.activity?.type === 'debate' ? undefined : 'auto'}
                                  className={item.activity?.type === 'debate' ? 'agenda-item-debate-timers-col' : 'agenda-item-timer-controls-col'}>
                                  {item.activity?.type === 'debate' ? <DebateAgendaTimers
                                    item={item} meeting={meeting} getMeeting={getMeeting}
                                    isTimerManager={isTimerManager} isLocked={isMeetingLocked} showClockText={showClockText}
                                    centerControl={visibilityToggle} showSpeaker={showCurrentSpeaker}
                                    speakerToggle={debateToggle('current speaker', showCurrentSpeaker, toggleCurrentSpeaker, 'bi-stopwatch', 'speaker')}
                                  /> : <AgendaItemTimerControls
                                    item={item}
                                    meeting={meeting}
                                    getMeeting={getMeeting}
                                    isTimerManager={isTimerManager}
                                    showClockText={showClockText}
                                    isLocked={isMeetingLocked}
                                  />}
                                </Col>}
                              </Row>
                            )}
                          </Col>
                        </Row>
                        <Row className='nextMeeting-overlay'></Row>
                      </div>
                    </Card.Body>
                    {hasPublishedTableTopicsEvaluation && (
                      <Card.Footer className="bg-transparent border-0 pt-0">
                        <Button
                          variant="outline-primary"
                          onClick={event => {
                            event.stopPropagation();
                            onOpenTableTopicsResults?.('tools', item.agendaItemId);
                          }}
                        >
                          <i className="bi bi-chat-quote-fill me-2" aria-hidden="true"></i>
                          View Table Topics Evaluation
                        </Button>
                      </Card.Footer>
                    )}
                  </Card>
                </div>
              </div>
              {/* Inline edit/delete: show only one editor at a time */}
              {showEditModal ? (
                <AgendaItemEditModal
                  item={item}
                  getMeeting={getMeeting}
                  show={true}
                  handleClose={handleCloseEditModal}
                />
              ) : showDeleteModal ? (
                <AgendaItemDeleteModal
                  item={item}
                  getMeeting={getMeeting}
                  show={true}
                  handleClose={handleCloseDeleteModal}
                />
              ) : null}
              {/* Main modal, shown on card click */}
              <AgendaItemModal
                agenda={agendaSnapshot}
                item={item}
                getMeeting={getMeeting}
                meeting={meeting}
                showClockText={showClockText}
                showModal={showModal}
                handleCloseModal={handleCloseModal}
              />
            </Col>
          </Row>
        </div>
      </div>
    </section >
  )
}
